import { Fragment, type Node as ProseMirrorNode } from '@tiptap/pm/model';

import { findFileIdByUrl } from './useBinaryFileHydration';

/** Query parameters that make a URL a bearer credential for a stored object — one that expires. */
const SIGNED_URL_PARAM = /[?&](X-Amz-(Signature|Credential|Security-Token)|Signature|AWSAccessKeyId|X-Goog-Signature)=/i;

/**
 * «Copy image» on a picture this app shows puts the `<img>` on the clipboard,
 * and its `src` is the presigned URL the page was showing — a link that dies
 * within the hour. Pasted back, that image would have no `fileId`. Every image
 * in a paste whose address this tab resolved is turned back into the file it
 * is, stored as `omb-file://<id>` like any upload.
 */
export const restorePastedFiles = (fragment: Fragment): Fragment => {
  const nodes: ProseMirrorNode[] = [];

  fragment.forEach((node) => {
    if (node.type.name === 'image' && !node.attrs.fileId && typeof node.attrs.src === 'string') {
      const fileId = findFileIdByUrl(node.attrs.src);

      nodes.push(fileId
        ? node.type.create({ ...node.attrs, fileId, src: `omb-file://${fileId}` })
        : node);

      return;
    }

    nodes.push(node.copy(restorePastedFiles(node.content)));
  });

  return Fragment.fromArray(nodes);
};

/**
 * True when a paste's html is the one image «Copy image» writes, behind a
 * signed address that no file of this tab answers to — copied in another tab,
 * or from another service's presigned link. `restorePastedFiles` cannot keep
 * such an image; the bytes on the clipboard beside it can.
 */
export const isUntraceableSignedImage = (html: string): boolean => {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const images = body.querySelectorAll('img');

  if (images.length !== 1 || body.textContent?.trim()) {
    return false;
  }

  const src = images[0]?.getAttribute('src') ?? '';

  return SIGNED_URL_PARAM.test(src) && findFileIdByUrl(src) === null;
};
